import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  getRollupLockStatus,
  forceLockRollup,
  unlockRollup,
  type ManagerLockRow,
  type ForceLockResult,
  type UnlockResult,
} from '../../api/adminClient';

// ============================================================================
// WEEK HELPERS (mirrors Submission/Delivery Monitor patterns)
// ============================================================================

function getCurrentWeek(): { week: number; year: number } {
  const now = new Date();
  const dayOfWeek = now.getUTCDay();
  const hours = now.getUTCHours();
  let daysSinceWeekStart = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  if (dayOfWeek === 1 && hours < 8) daysSinceWeekStart = 7;
  const weekStart = new Date(now);
  weekStart.setUTCDate(now.getUTCDate() - daysSinceWeekStart);
  weekStart.setUTCHours(8, 0, 0, 0);
  const jan1 = new Date(Date.UTC(weekStart.getUTCFullYear(), 0, 1));
  const days = Math.floor((weekStart.getTime() - jan1.getTime()) / (24 * 60 * 60 * 1000));
  const weekNumber = Math.ceil((days + jan1.getUTCDay() + 1) / 7);
  return { week: weekNumber, year: weekStart.getUTCFullYear() };
}

function getWeekStartDate(week: number, year: number): Date {
  const jan4 = new Date(year, 0, 4);
  const jan4DayOfWeek = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - (jan4DayOfWeek - 1));
  const targetMonday = new Date(week1Monday);
  targetMonday.setDate(week1Monday.getDate() + (week - 1) * 7);
  return targetMonday;
}

interface WeekInfo {
  week: number;
  start: Date;
  end: Date;
  label: string;
}

function getWeeksForMonth(year: number, month: number): WeekInfo[] {
  const weeks: WeekInfo[] = [];
  for (let w = 1; w <= 53; w++) {
    const start = getWeekStartDate(w, year);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    if (start.getMonth() === month && start.getFullYear() === year) {
      const label = `W${w}: ${start.getMonth() + 1}/${start.getDate()}–${end.getMonth() + 1}/${end.getDate()}`;
      weeks.push({ week: w, start, end, label });
    }
  }
  return weeks;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ============================================================================
// PAGE
// ============================================================================

type Action =
  | { kind: 'lock'; manager: ManagerLockRow; notifyUpstream: boolean }
  | { kind: 'unlock'; manager: ManagerLockRow };

type ActionResult =
  | { kind: 'lock'; result: ForceLockResult }
  | { kind: 'unlock'; result: UnlockResult };

export default function AdminRollupLocks() {
  const queryClient = useQueryClient();
  const current = useMemo(() => getCurrentWeek(), []);
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState(current.year);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedWeek, setSelectedWeek] = useState(current.week);

  const [pendingAction, setPendingAction] = useState<Action | null>(null);
  const [actionResult, setActionResult] = useState<ActionResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [unlockedOnly, setUnlockedOnly] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['rollup-locks', selectedYear, selectedWeek],
    queryFn: () => getRollupLockStatus(selectedYear, selectedWeek),
  });

  const lockMutation = useMutation({
    mutationFn: (args: { managerId: string; notifyUpstream: boolean }) =>
      forceLockRollup(args.managerId, selectedYear, selectedWeek, args.notifyUpstream),
    onSuccess: (result) => {
      setActionResult({ kind: 'lock', result });
      setPendingAction(null);
      queryClient.invalidateQueries({ queryKey: ['rollup-locks'] });
    },
    onError: (err: Error) => {
      setActionError(err.message || 'Force lock failed');
      setPendingAction(null);
    },
  });

  const unlockMutation = useMutation({
    mutationFn: (managerId: string) => unlockRollup(managerId, selectedYear, selectedWeek),
    onSuccess: (result) => {
      setActionResult({ kind: 'unlock', result });
      setPendingAction(null);
      queryClient.invalidateQueries({ queryKey: ['rollup-locks'] });
    },
    onError: (err: Error) => {
      setActionError(err.message || 'Unlock failed');
      setPendingAction(null);
    },
  });

  // Years dropdown — use current year + a few back since locks are tied to weeks; doesn't need DB lookup
  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = current.year - 2; y <= current.year + 1; y++) out.push(y);
    return out;
  }, [current.year]);

  const monthWeeks = useMemo(() => getWeeksForMonth(selectedYear, selectedMonth), [selectedYear, selectedMonth]);

  useEffect(() => {
    if (selectedYear === current.year) setSelectedMonth(currentMonth);
    else setSelectedMonth(0);
  }, [selectedYear]);

  useEffect(() => {
    const weeks = getWeeksForMonth(selectedYear, selectedMonth);
    if (weeks.length === 0) return;
    const inMonth = weeks.find((w) => w.week === current.week && selectedYear === current.year);
    setSelectedWeek(inMonth ? inMonth.week : weeks[weeks.length - 1].week);
  }, [selectedMonth, selectedYear]);

  const isMonthDisabled = (month: number) => {
    const now = new Date();
    if (selectedYear === now.getFullYear() && month > now.getMonth() + 1) return true;
    if (selectedYear > now.getFullYear()) return true;
    return false;
  };
  const isWeekFuture = (w: WeekInfo) => {
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
    return w.start > oneWeekFromNow;
  };

  const summary = status?.summary || { total_managers: 0, locked: 0, unlocked: 0 };
  const managers = useMemo(() => {
    const rows = status?.managers || [];
    return unlockedOnly ? rows.filter((m) => !m.locked) : rows;
  }, [status, unlockedOnly]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Manager Lock Status</h1>
        <p className="text-gray-500 mt-1">
          Stage 2 gate — managers must lock their team's reports before the scheduler can deliver them.
        </p>
      </div>

      {/* Week Picker */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex items-center gap-4 mb-3">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="flex gap-1 flex-wrap">
            {MONTH_NAMES.map((name, i) => {
              const disabled = isMonthDisabled(i);
              const isActive = i === selectedMonth;
              return (
                <button
                  key={i}
                  onClick={() => !disabled && setSelectedMonth(i)}
                  disabled={disabled}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-primary-600 text-white'
                      : disabled
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
                  )}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {monthWeeks.map((w) => {
            const isCurrent = w.week === current.week && selectedYear === current.year;
            const isSelected = w.week === selectedWeek;
            const future = isWeekFuture(w);
            return (
              <button
                key={w.week}
                onClick={() => !future && setSelectedWeek(w.week)}
                disabled={future}
                className={clsx(
                  'px-3 py-2 text-xs rounded-lg font-medium transition-colors',
                  future
                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                    : isSelected
                      ? 'bg-primary-600 text-white'
                      : isCurrent
                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100',
                )}
              >
                {w.label}
              </button>
            );
          })}
          {monthWeeks.length === 0 && <span className="text-sm text-gray-400">No weeks in this month</span>}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-4 flex items-center gap-4 text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={unlockedOnly}
            onChange={(e) => setUnlockedOnly(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span className="text-gray-700">Show unlocked managers only</span>
        </label>
        <span className="text-xs text-gray-500 ml-auto italic">
          The Sunday scheduler reads only locked team_rollups. Unlocked managers → empty downstream reports.
        </span>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <StatCard label="Total Managers" value={summary.total_managers} color="text-gray-900" />
        <StatCard label="Locked" value={summary.locked} color="text-green-600" />
        <StatCard label="Unlocked" value={summary.unlocked} color="text-red-600" />
      </div>

      {/* Manager Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            W{selectedWeek}-{selectedYear} — manager lock status
          </h2>
        </div>
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Loading…</div>
        ) : managers.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {unlockedOnly ? 'All managers are locked.' : 'No managers found (no users have direct reports).'}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left text-sm text-gray-500">
                <th className="px-4 py-3 font-medium">Manager</th>
                <th className="px-4 py-3 font-medium text-center">Direct Reports</th>
                <th className="px-4 py-3 font-medium text-center">Reports in Week</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Locked At</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Sent To</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {managers.map((m) => (
                <tr key={m.manager_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{m.manager_name}</div>
                    <div className="text-xs text-gray-500">{m.manager_email}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700">{m.direct_report_count}</td>
                  <td className="px-4 py-3 text-center text-gray-700">{m.reports_in_week}</td>
                  <td className="px-4 py-3"><LockBadge locked={m.locked} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {m.locked_at ? new Date(m.locked_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {m.source ? (
                      <span
                        className={clsx(
                          'inline-flex items-center px-2 py-0.5 rounded',
                          m.source === 'slack' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700',
                        )}
                      >
                        {m.source === 'slack' ? 'Slack (/rollup send)' : 'IT force-lock'}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-700">{m.sent_to_manager_name || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {m.locked ? (
                      <button
                        onClick={() => setPendingAction({ kind: 'unlock', manager: m })}
                        className="text-xs px-2 py-1 rounded text-red-700 hover:bg-red-50"
                      >
                        Unlock
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          setPendingAction({ kind: 'lock', manager: m, notifyUpstream: false })
                        }
                        className="text-xs px-2 py-1 rounded text-primary-700 hover:bg-primary-50"
                      >
                        Force Lock
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Confirm modal */}
      {pendingAction && pendingAction.kind === 'lock' && (
        <Modal onClose={() => setPendingAction(null)}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Force lock for testing?</h3>
          <p className="text-sm text-gray-600 mb-3">
            Lock W{selectedWeek}-{selectedYear} for <strong>{pendingAction.manager.manager_name}</strong>.
            This inserts a row in <code className="text-xs">team_rollups</code> so the scheduler will
            include their team's reports in the next delivery.
          </p>
          <p className="text-xs text-gray-500 mb-4">
            {pendingAction.manager.reports_in_week} report(s) currently fall in this week and will be
            included.
          </p>
          <label className="flex items-start gap-2 mb-4 cursor-pointer bg-amber-50 border border-amber-200 rounded-lg p-3">
            <input
              type="checkbox"
              checked={pendingAction.notifyUpstream}
              onChange={(e) =>
                setPendingAction({ ...pendingAction, notifyUpstream: e.target.checked })
              }
              className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-amber-900">
              <strong>Also DM the upstream manager</strong> (matches the real <code>/rollup send</code>
              behavior). Leave unchecked for silent testing — no Slack noise.
            </span>
          </label>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setPendingAction(null)}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() =>
                lockMutation.mutate({
                  managerId: pendingAction.manager.manager_id,
                  notifyUpstream: pendingAction.notifyUpstream,
                })
              }
              disabled={lockMutation.isPending}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {lockMutation.isPending ? 'Locking…' : 'Force Lock'}
            </button>
          </div>
        </Modal>
      )}

      {pendingAction && pendingAction.kind === 'unlock' && (
        <Modal onClose={() => setPendingAction(null)}>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unlock rollup?</h3>
          <p className="text-sm text-gray-600 mb-3">
            Delete the locked team_rollups row for <strong>{pendingAction.manager.manager_name}</strong>,
            W{selectedWeek}-{selectedYear}.
          </p>
          <p className="text-xs text-gray-500 mb-4">
            The scheduler will skip this manager's reports until someone re-locks. The manager can
            then re-run <code>/rollup send</code> from Slack, or you can Force Lock again.
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setPendingAction(null)}
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={() => unlockMutation.mutate(pendingAction.manager.manager_id)}
              disabled={unlockMutation.isPending}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {unlockMutation.isPending ? 'Unlocking…' : 'Unlock'}
            </button>
          </div>
        </Modal>
      )}

      {/* Error modal */}
      {actionError && (
        <Modal onClose={() => setActionError(null)}>
          <h3 className="text-lg font-semibold text-red-700 mb-2">⚠ Action failed</h3>
          <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{actionError}</p>
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setActionError(null)}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </Modal>
      )}

      {/* Result modal */}
      {actionResult && (
        <Modal onClose={() => setActionResult(null)}>
          {actionResult.kind === 'lock' ? (
            <LockResult result={actionResult.result} />
          ) : (
            <UnlockResultPanel result={actionResult.result} />
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setActionResult(null)}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================================
// SUBCOMPONENTS
// ============================================================================

function StatCard({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <p className={clsx('text-3xl font-bold', color)}>{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}

function LockBadge({ locked }: { locked: boolean }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        locked ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
      )}
    >
      {locked ? '🔒 Locked' : '🔓 Unlocked'}
    </span>
  );
}

function LockResult({ result }: { result: ForceLockResult }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        🔒 Locked {result.manager_name}
      </h3>
      <ul className="text-sm text-gray-700 space-y-1">
        <li>
          <strong>{result.locked_report_count}</strong> report{result.locked_report_count === 1 ? '' : 's'} included
        </li>
        <li>
          Upstream DM:{' '}
          {result.notified_upstream
            ? `sent to ${result.upstream_manager_name}`
            : result.upstream_manager_name
              ? `skipped (would have gone to ${result.upstream_manager_name})`
              : 'no upstream manager configured'}
        </li>
      </ul>
      {result.warnings.length > 0 && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2">
          <p className="text-xs font-medium text-amber-800 mb-1">Warnings</p>
          <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5">
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function UnlockResultPanel({ result }: { result: UnlockResult }) {
  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        {result.unlocked ? '🔓 Unlocked' : '⚠ Nothing to unlock'} — {result.manager_name}
      </h3>
      <p className="text-sm text-gray-600">
        {result.unlocked
          ? 'The team_rollups row was deleted. The scheduler will skip this manager until someone re-locks.'
          : 'There was no existing lock for this manager + week.'}
      </p>
    </div>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}
